import {
  effect,
  exhaustMapWithTrailing,
  fromPromise,
  LiveData,
  onComplete,
  OnEvent,
  onStart,
  Service,
  smartRetry,
} from '@toeverything/infra';
import { catchError, EMPTY, tap } from 'rxjs';

import type { AuthAccountInfo } from '../entities/session';
import { AccountChanged } from '../events/account-changed';
import type {
  UpdateUserSettingsInput,
  UserSettings,
  UserSettingsStore,
} from '../stores/user-settings';

export type { UserSettings };

/**
 * The signed-in user's settings. On every sign-in it stores this device's
 * time zone when it differs from the saved one, because a date-time without
 * offset is that wall time in the zone (a day is UTC midnight).
 */
@OnEvent(AccountChanged, e => e.onAccountChanged)
export class UserSettingsService extends Service {
  constructor(private readonly store: UserSettingsStore) {
    super();
  }

  userSettings$ = new LiveData<UserSettings | undefined>(undefined);
  isLoading$ = new LiveData<boolean>(false);
  error$ = new LiveData<any | undefined>(undefined);

  revalidate = effect(
    exhaustMapWithTrailing(() => {
      return fromPromise(() => {
        return this.store.getUserSettings();
      }).pipe(
        smartRetry(),
        tap(settings => {
          this.userSettings$.value = settings;
        }),
        catchError(error => {
          this.error$.value = error;
          return EMPTY;
        }),
        onStart(() => {
          this.isLoading$.value = true;
        }),
        onComplete(() => {
          this.isLoading$.value = false;
        })
      );
    })
  );

  private onAccountChanged(account: AuthAccountInfo | null) {
    if (!account) return;
    this.reportTimeZone().catch(error => {
      this.error$.value = error;
    });
  }

  /** Stores this device's time zone when it differs from the saved one. */
  async reportTimeZone(): Promise<void> {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const settings = await this.store.getUserSettings();
    if (settings?.timeZone === timeZone) return;
    await this.updateUserSettings({ timeZone });
  }

  async updateUserSettings(settings: UpdateUserSettingsInput) {
    await this.store.updateUserSettings(settings);
    this.userSettings$.value = {
      ...this.userSettings$.value,
      ...(settings as UserSettings),
    };
    this.revalidate();
  }
}
